import GuideLayout from "./GuideLayout";
import { VectorVsRasterFigure } from "./GuideFigures";

export default function VectorVsRaster() {
  return (
    <GuideLayout
      title="Vector vs Raster Graphics: What's the Difference?"
      description="Vector vs raster explained simply: how each stores an image, why EPS and SVG scale forever while PNG and JPG blur, how DPI really works, and why converting back is so hard."
      path="/guides/vector-vs-raster"
      updated="August 1, 2026"
      related={["/guides/convert-eps-to-png", "/guides/eps-vs-svg-vs-ai-vs-pdf", "/guides/what-is-an-eps-file"]}
    >
      <p className="lead">
        Almost every confusing thing about image files traces back to one distinction:
        whether the file stores <em>instructions for drawing</em> a picture, or a
        <em> record of the finished pixels</em>. That single difference explains why an EPS
        logo stays razor-sharp on a billboard while a PNG of the same logo falls apart
        when you enlarge it.
      </p>

      <h2>The short version</h2>
      <p>
        A <strong>vector</strong> file is a recipe. It says: draw a circle here with this
        radius, fill it with this blue, put this curve beside it. Nothing about size is
        fixed, so the recipe can be followed at any scale.
      </p>
      <p>
        A <strong>raster</strong> file is a photograph of the finished cake. It records
        the colour of every pixel in a fixed grid. You can shrink it, but you cannot
        enlarge it, because the detail was never captured.
      </p>

      <VectorVsRasterFigure />

      <h2>What is a vector graphic?</h2>
      <p>
        A vector image stores artwork as mathematics — anchor points, Bézier curves,
        strokes and fills. Rendering software solves that maths for whatever size you ask
        for, so the output is calculated fresh at your screen&apos;s or printer&apos;s full
        resolution every time.
      </p>
      <p>
        <strong>EPS, SVG, AI and PDF</strong> are the common vector formats. They are the
        correct choice for logos, icons, typography, diagrams and line art — anything made
        of defined shapes and flat colour.
      </p>

      <h2>What is a raster graphic?</h2>
      <p>
        A raster (bitmap) image is a fixed grid of coloured pixels. A 1000×1000 PNG holds
        exactly one million colour values, and that is the entire picture. This captures
        subtle gradation beautifully, which is why every photograph is raster — no
        reasonable set of curves could describe a human face.
      </p>
      <p>
        <strong>PNG, JPG, GIF and WebP</strong> are the common raster formats. They are
        right for photographs and anything with organic, continuously varying detail.
      </p>

      <h2>Side-by-side comparison</h2>
      <div className="table-wrap">
        <table className="tbl">
          <thead><tr><th></th><th>Vector</th><th>Raster</th></tr></thead>
          <tbody>
            <tr><td>Made of</td><td>Mathematical paths</td><td>A grid of pixels</td></tr>
            <tr><td>Scaling up</td><td>Infinite and lossless</td><td>Blurs or goes blocky</td></tr>
            <tr><td>Has a resolution?</td><td>No</td><td>Yes, fixed at creation</td></tr>
            <tr><td>File size driven by</td><td>Artwork complexity</td><td>Pixel dimensions</td></tr>
            <tr><td>Editable afterwards?</td><td>Every shape, individually</td><td>Only the pixels</td></tr>
            <tr><td>Handles photographs</td><td>Poorly</td><td>Excellently</td></tr>
            <tr><td>Examples</td><td>EPS, SVG, AI, PDF</td><td>PNG, JPG, GIF, WebP</td></tr>
          </tbody>
        </table>
      </div>

      <h2>File size behaves in opposite ways</h2>
      <p>This surprises people, and it follows directly from how each format works.</p>
      <p>
        <strong>Vector size depends on complexity, not dimensions.</strong> A simple
        two-colour logo might be 15 KB whether it is printed on a stamp or a building —
        the instructions are identical. But a detailed map with a hundred thousand paths
        can become enormous, and slow to render.
      </p>
      <p>
        <strong>Raster size depends on dimensions, not complexity.</strong> A plain white
        4000×4000 image still has sixteen million pixels to account for. Doubling the width
        and height roughly quadruples the pixel count.
      </p>
      <p>
        The practical consequence: for a simple logo, vector is almost always both sharper
        and smaller. For a photograph, vector is not an option at all.
      </p>

      <h2>How DPI actually works</h2>
      <p>
        DPI (dots per inch) is a common source of confusion, because{" "}
        <strong>vector files do not have a DPI</strong>. Resolution only comes into
        existence when the artwork is rendered to pixels.
      </p>
      <p>
        The arithmetic is straightforward: pixels needed = physical size in inches × DPI.
        A 4-inch-wide graphic at 300 DPI needs 1200 pixels. The same graphic on screen at
        96 DPI needs about 384.
      </p>
      <p>
        So when you <a href="/guides/convert-eps-to-png">export an EPS to PNG</a>, you are
        choosing a DPI whether you realise it or not. Rules of thumb: 300 DPI for print,
        96 DPI for screen, 150 DPI for something being viewed at arm&apos;s length. Large
        banners are often produced at far lower DPI because nobody stands two feet from a
        billboard.
      </p>

      <h2>Converting is a one-way street</h2>
      <p>
        <strong>Vector to raster is easy.</strong> Rasterizing just means running the
        instructions and recording the result. Every conversion tool does this.
      </p>
      <p>
        <strong>Raster to vector is genuinely hard.</strong> Going backwards means looking
        at pixels and guessing what shapes produced them — a problem with no correct
        answer. Auto-tracing tools (Illustrator&apos;s Image Trace, Inkscape&apos;s Trace
        Bitmap) do a decent job on high-contrast, flat-colour images such as a black
        silhouette. They do poorly on photographs, soft gradients, small text and anything
        with anti-aliased edges, producing hundreds of ragged paths that only approximate
        the original.
      </p>
      <p>
        This is exactly why designers insist on keeping the source file. Once a logo exists
        only as a 500-pixel PNG, getting a print-quality version usually means redrawing it
        from scratch.
      </p>

      <h2>Choosing in practice</h2>
      <ul>
        <li><strong>Logo or icon:</strong> vector always. Keep an EPS, AI or SVG master, and export rasters as needed.</li>
        <li><strong>Photograph:</strong> raster always. JPG for photos, PNG when you need transparency or perfectly crisp edges.</li>
        <li><strong>Logo on a website:</strong> SVG if you can — it is small, sharp on every display, and stylable with CSS. Otherwise a PNG at twice the display size for high-density screens.</li>
        <li><strong>Anything going to print:</strong> vector, or a raster at 300 DPI at final size.</li>
        <li><strong>Large-format banner or vehicle wrap:</strong> vector, without exception.</li>
        <li><strong>Screenshot or UI mockup:</strong> raster — PNG, since JPG smears text.</li>
      </ul>

      <h2>Common mistakes</h2>
      <ul>
        <li><strong>Enlarging a small raster and hoping.</strong> Upscaling invents pixels; it cannot recover detail that was never recorded.</li>
        <li><strong>Treating a PNG as a master copy.</strong> Always archive the vector.</li>
        <li><strong>Assuming a vector file is safe from rasterization.</strong> An EPS can contain an embedded photograph, in which case that part is still pixel-limited.</li>
        <li><strong>Sending a printer a 72-DPI web image.</strong> At 300 DPI it will print at roughly a quarter of the size you expect.</li>
        <li><strong>Using JPG for line art.</strong> JPG compression is built for photographs and leaves visible halos around hard edges.</li>
      </ul>

      <h2>Frequently asked questions</h2>
      <dl className="guide-faq">
        <dt>Can you convert raster back to vector?</dt>
        <dd>Only by tracing, automatically or by hand, which approximates the shapes rather than recovering them. It works acceptably for simple high-contrast images and badly for photographs.</dd>
        <dt>Is EPS vector or raster?</dt>
        <dd>Primarily vector, but EPS can embed raster images inside it. A pure-vector EPS scales infinitely; one containing a photograph is limited by that photograph&apos;s resolution.</dd>
        <dt>Is PDF vector or raster?</dt>
        <dd>Either, or both. PDF is a container: it may hold vector artwork, embedded photographs, or a scanned page that is entirely pixels.</dd>
        <dt>Why does my logo look blurry on a retina screen?</dt>
        <dd>You are serving a raster image at the same pixel size as a standard display, and the screen is showing it at roughly double density. Use SVG, or export the PNG at 2× the display size.</dd>
        <dt>Does a vector file have a DPI?</dt>
        <dd>No. Resolution only exists once the file is rendered to pixels. Any DPI setting you see in an export dialog describes the output you are about to create.</dd>
        <dt>Which is better?</dt>
        <dd>Neither — they solve different problems. Use vector for designed artwork made of shapes, raster for captured images of the real world.</dd>
      </dl>
    </GuideLayout>
  );
}
